import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip,
  Button, IconButton, Alert, AlertTitle, CircularProgress, Tabs, Tab,
  List, ListItem, ListItemText, ListItemIcon, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Tooltip
} from '@mui/material';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Warning as WarningIcon,
  InfoOutlined as InfoIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  BarChart as BarChartIcon,
  ShowChart as ShowChartIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ErrorOutline as ErrorIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import RiskManagerService from '../services/RiskManagerService';

function RiskManagement() {
  const [portfolioId] = useState('default');
  const [riskData, setRiskData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  
  const fetchRiskData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Communicate with the risk manager node through the service
      const data = await RiskManagerService.getRiskAnalysis(portfolioId);
      setRiskData(data);
    } catch (err) {
      console.error('Error fetching risk data:', err);
      setError('Failed to fetch risk data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Load initial data when component mounts
  useEffect(() => {
    fetchRiskData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioId]);
  
  // Get alert count for badge
  const getActiveAlertCount = () => {
    if (!riskData || !riskData.riskAlerts) return 0;
    return riskData.riskAlerts.filter(alert => alert.status === 'active').length;
  };
  
  // Helper component for displaying risk metrics with trend indicators
  const RiskMetricCard = ({ title, value, change, unit = '', description, icon, reverseColors = false }) => {
    const isPositive = change ? parseFloat(change) > 0 : false;
    const isNegative = change ? parseFloat(change) < 0 : false;
    const color = (reverseColors ? !isPositive : isPositive) ? 'success.main' : (isNegative ? 'error.main' : 'text.secondary');
    
    return (
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="subtitle1" color="text.secondary">{title}</Typography>
            {icon && icon}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
            <Typography variant="h4" component="span">
              {value}{unit}
            </Typography>
            {change && (
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                {isPositive ? <TrendingUpIcon fontSize="small" sx={{ color }} /> : 
                isNegative ? <TrendingDownIcon fontSize="small" sx={{ color }} /> : null}
                <Typography variant="body2" sx={{ color }}>
                  {isPositive ? '+' : ''}{change}%
                </Typography>
              </Box>
            )}
          </Box>
          {description && (
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };
  
  // Severity indicator for alerts
  const SeverityIndicator = ({ severity }) => {
    const color = severity === 'high' ? 'error' : 
                  severity === 'medium' ? 'warning' : 'info';
    
    return (
      <Chip 
        label={severity.charAt(0).toUpperCase() + severity.slice(1)} 
        color={color}
        size="small"
      />
    );
  };
  
  // Function to resolve risk alerts
  const handleResolveAlert = async (alertId) => {
    try {
      await RiskManagerService.resolveAlert(alertId);
      // Re-fetch data to reflect changes
      fetchRiskData();
    } catch (err) {
      console.error('Error resolving alert:', err);
      setError('Failed to resolve alert. Please try again.');
    }
  };
  
  return (
    <Box>
      {/* Header with action buttons */}
      <Box sx={{
        mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
        p: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AccountBalanceWalletIcon sx={{ fontSize: 28, mr: 1, color: '#00FF94' }} />
          <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: 'white' }}>
            Risk Management
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            startIcon={<SettingsIcon />}
          >
            Risk Settings
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<ShowChartIcon />}
          >
            Stress Test
          </Button>
          <IconButton color="primary" onClick={fetchRiskData}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Analyzing portfolio risk...
          </Typography>
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      ) : riskData ? (
        <Box>
          {/* Alert banner if there are active alerts */}
          {getActiveAlertCount() > 0 && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
            >
              <AlertTitle>Risk Alerts: {getActiveAlertCount()}</AlertTitle>
              You have {getActiveAlertCount()} active risk alerts that need your attention.
            </Alert>
          )}
          
          {/* Main content area with tabs */}
          <Paper sx={{ mb: 4 }}>
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Overview" icon={<InfoIcon />} />
              <Tab label="Position Risk" icon={<BarChartIcon />} />
              <Tab label="Risk Metrics" icon={<ShowChartIcon />} />
              <Tab 
                label="Alerts" 
                icon={<WarningIcon />}
                iconPosition="start"
                sx={{ color: getActiveAlertCount() > 0 ? 'warning.main' : 'inherit' }}
              />
            </Tabs>
            
            {/* Tab content */}
            <Box sx={{ p: 3, minHeight: '500px' }}>
              {activeTab === 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Portfolio Risk Overview</Typography>
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Portfolio Value"
                        value={`$${riskData.portfolioSummary.totalValue}`}
                        change={riskData.portfolioSummary.pnl.daily}
                        description="Current total portfolio value"
                        icon={<AccountBalanceWalletIcon color="primary" />}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Daily P&L"
                        value={`$${riskData.portfolioSummary.pnl.daily}`}
                        change={(riskData.portfolioSummary.pnl.daily / (riskData.portfolioSummary.totalValue - riskData.portfolioSummary.pnl.daily) * 100).toFixed(2)}
                        description="Profit/Loss for current trading day"
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Net Exposure"
                        value={`${riskData.portfolioSummary.exposures.net}%`}
                        reverseColors={true}
                        description="Net market exposure (long - short)"
                      />
                    </Grid>
                  </Grid>
                  
                  <Typography variant="subtitle1" gutterBottom>Asset Allocation</Typography>
                  <Grid container spacing={2} sx={{ mb: 4 }}>
                    {Object.entries(riskData.portfolioSummary.allocations).map(([key, value]) => (
                      <Grid item xs={6} sm={4} md={2} key={key}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center', py: 1 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {key.charAt(0).toUpperCase() + key.slice(1)}
                            </Typography>
                            <Typography variant="h6">
                              {value}%
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                  
                  <Typography variant="subtitle1" gutterBottom>Risk Summary</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Metric</TableCell>
                              <TableCell align="right">Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Daily Value at Risk (VaR)</TableCell>
                              <TableCell align="right">{riskData.riskMetrics.valueAtRisk.daily.percent}%</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Expected Shortfall</TableCell>
                              <TableCell align="right">{riskData.riskMetrics.expectedShortfall.percent}%</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Sharpe Ratio</TableCell>
                              <TableCell align="right">{riskData.riskMetrics.sharpeRatio}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Current Drawdown</TableCell>
                              <TableCell align="right">{riskData.riskMetrics.maxDrawdown.current}%</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Beta to Market</TableCell>
                              <TableCell align="right">{riskData.riskMetrics.beta}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Stress Test Scenarios</Typography>
                          <List dense>
                            {Object.entries(riskData.riskMetrics.stressTestResults).map(([scenario, impact]) => (
                              <ListItem key={scenario}>
                                <ListItemIcon>
                                  {parseFloat(impact) < -10 ? 
                                    <ErrorIcon color="error" /> : 
                                    parseFloat(impact) < 0 ? 
                                      <WarningIcon color="warning" /> : 
                                      <CheckIcon color="success" />}
                                </ListItemIcon>
                                <ListItemText 
                                  primary={scenario.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                  secondary={`Impact: ${impact}%`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Position Risk Analysis</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Symbol</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Position</TableCell>
                          <TableCell align="right">Size ($)</TableCell>
                          <TableCell align="right">Allocation (%)</TableCell>
                          <TableCell align="right">P&L ($)</TableCell>
                          <TableCell align="right">VaR ($)</TableCell>
                          <TableCell align="right">Stop Loss</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {riskData.positionRisks.map((position) => (
                          <TableRow key={position.symbol} hover>
                            <TableCell>{position.symbol}</TableCell>
                            <TableCell>{position.type}</TableCell>
                            <TableCell>
                              <Chip 
                                label={position.position.toUpperCase()} 
                                color={position.position === 'long' ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{position.size}</TableCell>
                            <TableCell align="right">{position.allocation}%</TableCell>
                            <TableCell align="right" sx={{ 
                              color: parseFloat(position.unrealizedPnl) > 0 ? 'success.main' : 
                                     parseFloat(position.unrealizedPnl) < 0 ? 'error.main' : 'inherit'
                            }}>
                              {parseFloat(position.unrealizedPnl) > 0 ? '+' : ''}{position.unrealizedPnl}
                            </TableCell>
                            <TableCell align="right">{position.valueAtRisk}</TableCell>
                            <TableCell align="right">
                              <Tooltip title={`${position.stopLoss.type} stop at ${position.stopLoss.price}`}>
                                <span>{position.stopLoss.distance}%</span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              
              {activeTab === 2 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Risk Metrics & Analysis</Typography>
                  
                  <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Value at Risk (1-day)"
                        value={`${riskData.riskMetrics.valueAtRisk.daily.percent}%`}
                        description={`$${riskData.riskMetrics.valueAtRisk.daily.amount} at 95% confidence`}
                        reverseColors={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Expected Shortfall"
                        value={`${riskData.riskMetrics.expectedShortfall.percent}%`}
                        description={`$${riskData.riskMetrics.expectedShortfall.amount} tail risk measure`}
                        reverseColors={true}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RiskMetricCard 
                        title="Maximum Drawdown"
                        value={`${riskData.riskMetrics.maxDrawdown.current}%`}
                        description={`Historical high: ${riskData.riskMetrics.maxDrawdown.historical}%`}
                        reverseColors={true}
                      />
                    </Grid>
                  </Grid>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>Performance Metrics</Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Sharpe Ratio</TableCell>
                                  <TableCell align="right">
                                    {riskData.riskMetrics.sharpeRatio}
                                    <Chip 
                                      size="small"
                                      label={parseFloat(riskData.riskMetrics.sharpeRatio) > 1 ? "Good" : "Poor"}
                                      color={parseFloat(riskData.riskMetrics.sharpeRatio) > 1 ? "success" : "error"}
                                      sx={{ ml: 1 }}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Sortino Ratio</TableCell>
                                  <TableCell align="right">
                                    {riskData.riskMetrics.sortinoRatio}
                                    <Chip 
                                      size="small"
                                      label={parseFloat(riskData.riskMetrics.sortinoRatio) > 1.2 ? "Good" : "Poor"}
                                      color={parseFloat(riskData.riskMetrics.sortinoRatio) > 1.2 ? "success" : "error"}
                                      sx={{ ml: 1 }}
                                    />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Beta</TableCell>
                                  <TableCell align="right">{riskData.riskMetrics.beta}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>Alpha</TableCell>
                                  <TableCell align="right">{riskData.riskMetrics.alpha}%</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {activeTab === 3 && (
                <Box>
                  <Typography variant="h6" gutterBottom>Risk Alerts & Notifications</Typography>
                  
                  {riskData.riskAlerts.length > 0 ? (
                    <List>
                      {riskData.riskAlerts.map((alert) => (
                        <Paper 
                          key={alert.id} 
                          variant="outlined" 
                          sx={{ mb: 2, borderLeft: 4, borderColor: 
                            alert.severity === 'high' ? 'error.main' : 
                            alert.severity === 'medium' ? 'warning.main' : 'info.main' 
                          }}
                        >
                          <ListItem
                            secondaryAction={
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                                onClick={() => handleResolveAlert(alert.id)}
                              >
                                Resolve
                              </Button>
                            }
                          >
                            <ListItemIcon>
                              {alert.severity === 'high' ? <ErrorIcon color="error" /> : 
                               alert.severity === 'medium' ? <WarningIcon color="warning" /> : 
                               <InfoIcon color="info" />}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {alert.title}
                                  <SeverityIndicator severity={alert.severity} sx={{ ml: 1 }} />
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {alert.description}
                                  </Typography>
                                  <Typography variant="caption" component="div" color="text.secondary">
                                    {new Date(alert.timestamp).toLocaleString()}
                                  </Typography>
                                </>
                              }
                            />
                          </ListItem>
                        </Paper>
                      ))}
                    </List>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <CheckIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                      <Typography variant="h6">No Active Alerts</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your portfolio is currently within all risk parameters.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No Risk Data Available
          </Typography>
          <Typography variant="body1">
            Unable to retrieve risk data from risk manager node.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<RefreshIcon />}
            onClick={fetchRiskData}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Paper>
      )}
    </Box>
  );
}

export default RiskManagement;
